import { useGetForumGroupsQuery } from "api/queries/groups";
import SelectionSiteStatus from "backend-models/selectionSiteStatus";
import { Button, Form, Input, Select } from "components/styleguide";
import DatePickerAvailableDaysWrapper from "components/styleguide/components/DatePickerAvailableDaysWrapper";
import AttendeeStatus from "enums/AttendeeStatus";
import { toNumber } from "lodash";
import moment from "moment";
import PropTypes from "prop-types";
import React from "react";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";

import { useGetForumByIdQuery } from "../../../api/queries/forums";
import FullHeightForm from "../../../components/FullHeightForm";
const { Option } = Select;

const SelectionSiteForm = ({
  onSubmit,
  selectionSite,
  errorMsg,
  onDrawerClose,
  onEditClose,
  isMutationLoading,
  isEdit,
}) => {
  const { t } = useTranslation();
  const { id: forumId } = useOutletContext();

  const { data: { data: forum = {} } = {} } = useGetForumByIdQuery({ forumId: toNumber(forumId) });
  const forumStartDate = moment(forum.startDate);
  const defaultAvailabilityDate = forumStartDate.isValid() ? forumStartDate.clone().subtract(1, "days") : undefined;

  const { isLoading: isGroupsLoading, data: { data: groups = [] } = {} } = useGetForumGroupsQuery({
    forumId,
    attendeeStatuses: [AttendeeStatus.Attending, AttendeeStatus.AttendingReplacement],
  });

  return (
    <FullHeightForm
      id="newSelectionSiteForm"
      name="new-selection-site"
      labelCol={{ justify: "left", span: 6 }}
      wrapperCol={{ justify: "right" }}
      labelWrap
      key={JSON.stringify({ selectionSite, forumStartDate: forum.startDate })}
      destroyOnClose
      actionsPrepend={<Button onClick={isEdit ? onEditClose : onDrawerClose}>{t("Cancel")}</Button>}
      actionsAppend={
        <Button type="primary" htmlType="submit" loading={isMutationLoading}>
          {isEdit ? t("Save") : t("Create")}
        </Button>
      }
      onFinish={onSubmit}
    >
      <Form.Item
        label={t("Name")}
        name="name"
        initialValue={selectionSite?.name}
        rules={[
          {
            required: true,
            message: t("Name is required!"),
          },
        ]}
      >
        <Input />
      </Form.Item>
      <Form.Item label={t("Description")} name="description" initialValue={selectionSite?.description}>
        <Input />
      </Form.Item>
      <Form.Item
        label={t("Available")}
        name="available"
        initialValue={[
          selectionSite?.availableFrom ? moment(selectionSite?.availableFrom) : defaultAvailabilityDate,
          selectionSite?.availableTo ? moment(selectionSite?.availableTo) : defaultAvailabilityDate,
        ]}
        rules={[
          {
            required: true,
            message: t("Availability is required!"),
          },
          {
            validator(_, value) {
              if (!value?.[0] || !value?.[1]) {
                return Promise.resolve();
              }

              if (!value[1].isAfter(value[0], "day")) {
                return Promise.reject(new Error(t("End date should be greater then start date")));
              }

              return Promise.resolve();
            },
          },
          {
            validator(_, value) {
              if (!forumStartDate.isValid() || !value?.[0] || !value?.[1]) {
                return Promise.resolve();
              }

              if (value.some((date) => !date.isBefore(forumStartDate, "day"))) {
                return Promise.reject(new Error(t("Date selected must be before the event start date")));
              }

              return Promise.resolve();
            },
          },
        ]}
      >
        <DatePickerAvailableDaysWrapper.RangePickerAvailableDaysWrapper
          placeholder={[t("From"), t("To")]}
          eventStartDate={moment(forum?.startDate)}
          eventEndDate={moment(forum?.endDate)}
          availableDatesGetterType={"TwelveMonthsBeforeStartOfEvent"}
          defaultPickerValue={[
            selectionSite?.availableFrom ? moment(selectionSite?.availableFrom) : defaultAvailabilityDate,
            selectionSite?.availableTo ? moment(selectionSite?.availableTo) : defaultAvailabilityDate,
          ]}
        />
      </Form.Item>
      <Form.Item
        label={t("Group")}
        name="groupId"
        initialValue={selectionSite?.groupId}
        rules={[
          {
            required: true,
            message: t("Group is required!"),
          },
        ]}
      >
        <Select
          loading={isGroupsLoading}
          showSearch
          optionFilterProp={"label"}
          options={groups.map(({ id, name }) => ({ value: id, label: name }))}
        />
      </Form.Item>
      <Form.Item label={t("Color scheme")} name="colorScheme" initialValue={selectionSite?.colorScheme || "Default"}>
        <Select>
          <Option value="default" key="default">
            {t("Default")}
          </Option>
        </Select>
      </Form.Item>
      <Form.Item
        label={t("Status")}
        name="status"
        initialValue={selectionSite?.status || SelectionSiteStatus.VALUES[1]}
        rules={[
          {
            required: true,
            message: t("Status is required!"),
          },
        ]}
      >
        <Select>
          {SelectionSiteStatus.LIST.map(({ value, label }) => (
            <Select.Option key={value} value={value}>
              {t(label)}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
      {errorMsg && <p style={{ color: "red", float: "right" }}>{errorMsg}</p>}
    </FullHeightForm>
  );
};

SelectionSiteForm.propTypes = {
  onSubmit: PropTypes.func,
  selectionSite: PropTypes.object,
  groups: PropTypes.array,
  errorMsg: PropTypes.string,
  onDrawerClose: PropTypes.func,
  onEditClose: PropTypes.func,
  isMutationLoading: PropTypes.bool,
  isEdit: PropTypes.bool,
};

export default SelectionSiteForm;
